import React, { useEffect, useRef } from "react";

// Shared confirmation dialog. Replaces window.confirm and the copy-pasted inline modal
// markup: same overlay/card visuals as the existing dialogs, plus the keyboard behavior
// they lack - focus moves into the dialog on open (Cancel first, the safe default),
// Tab cycles within it, Escape cancels, and focus returns to the trigger on close.
//
// Props:
//   open          - render/show the dialog
//   title         - required heading
//   body          - optional string or node under the title
//   confirmLabel  - confirm button text (default "Confirm")
//   cancelLabel   - cancel button text (default "Cancel")
//   tone          - "danger" (red confirm) | "primary" (blue confirm)
//   busy          - disables both buttons (async confirm in flight)
//   onConfirm / onCancel
export default function ConfirmDialog({
  open,
  title,
  body = null,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "primary",
  busy = false,
  onConfirm,
  onCancel,
}) {
  const dialogRef = useRef(null);
  const cancelRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    if (!open) return undefined;
    previouslyFocusedRef.current = document.activeElement;
    const focusTimer = setTimeout(() => cancelRef.current?.focus(), 0);

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCancelRef.current?.();
        return;
      }
      if (e.key !== "Tab") return;
      const focusables = dialogRef.current?.querySelectorAll("button:not([disabled])");
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (!dialogRef.current?.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKeyDown, true);
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === "function") prev.focus();
    };
  }, [open]);

  if (!open) return null;

  const confirmClass =
    tone === "danger"
      ? "bg-red-600 hover:bg-red-500 text-white"
      : "bg-blue-600 hover:bg-blue-500 text-white";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div ref={dialogRef} className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl">
        <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</p>
        {body ? <div className="mt-2 text-sm text-gray-700 dark:text-gray-200">{body}</div> : null}
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            ref={cancelRef}
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`px-4 py-2 rounded-xl disabled:opacity-60 disabled:cursor-not-allowed ${confirmClass}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
